/**
 * IndexedDB-based storage for large datasets
 * Replaces localStorage to handle 2000+ parking spots
 */

import { log, LogCategory } from './logger'

const DB_NAME = 'map-editor-db';
const DB_VERSION = 1;
const STORE_NAME = 'data';

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Initialize and get IndexedDB connection
 */
function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      log.error(LogCategory.STATE, 'Failed to open IndexedDB', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });

  return dbPromise;
}

/**
 * Async IndexedDB storage operations
 */
export const indexedDBStorage = {
  async get<T>(key: string, defaultValue: T): Promise<T> {
    try {
      const db = await getDB();
      return new Promise((resolve) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result !== undefined ? request.result : defaultValue);
        };

        request.onerror = () => {
          log.error(LogCategory.STATE, `Failed to read from IndexedDB (${key})`, request.error);
          resolve(defaultValue);
        };
      });
    } catch (error) {
      log.error(LogCategory.STATE, `Failed to get from IndexedDB (${key})`, error);
      return defaultValue;
    }
  },

  async set<T>(key: string, value: T): Promise<boolean> {
    try {
      const db = await getDB();
      return new Promise((resolve) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(value, key);

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          log.error(LogCategory.STATE, `Failed to write to IndexedDB (${key})`, request.error);
          resolve(false);
        };
      });
    } catch (error) {
      log.error(LogCategory.STATE, `Failed to set in IndexedDB (${key})`, error);
      return false;
    }
  },

  async remove(key: string): Promise<boolean> {
    try {
      const db = await getDB();
      return new Promise((resolve) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          log.error(LogCategory.STATE, `Failed to remove from IndexedDB (${key})`, request.error);
          resolve(false);
        };
      });
    } catch (error) {
      log.error(LogCategory.STATE, `Failed to remove from IndexedDB (${key})`, error);
      return false;
    }
  },

  async clear(): Promise<boolean> {
    try {
      const db = await getDB();
      return new Promise((resolve) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          log.error(LogCategory.STATE, 'Failed to clear IndexedDB', request.error);
          resolve(false);
        };
      });
    } catch (error) {
      log.error(LogCategory.STATE, 'Failed to clear IndexedDB', error);
      return false;
    }
  },

  /**
   * Get storage size estimate
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0,
        };
      } catch (error) {
        log.error(LogCategory.STATE, 'Failed to get storage estimate', error);
        return null;
      }
    }
    return null;
  },
};

/**
 * Synchronous wrapper that caches data in memory
 * For backwards compatibility with existing store code
 */
class SyncStorageWrapper {
  private cache: Map<string, any> = new Map();
  private initialized = false;
  private onReadyCallbacks: Array<() => void> = [];

  /**
   * Register callback to run when storage init completes.
   * If already initialized, callback fires immediately.
   */
  onReady(callback: () => void) {
    if (this.initialized) {
      callback();
    } else {
      this.onReadyCallbacks.push(callback);
    }
  }
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.loadAllData();
    await this.initPromise;
    this.initialized = true;
  }

  private async loadAllData(): Promise<void> {
    try {
      const db = await getDB();
      const loadedKeys: string[] = [];

      return new Promise((resolve) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.openCursor();

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const key = cursor.key as string;
            this.cache.set(key, cursor.value);
            loadedKeys.push(key);
            cursor.continue();
          } else {
            // 로드 완료 후 로깅
            if (loadedKeys.length > 0) {
              log.info(LogCategory.STATE, `Loaded ${loadedKeys.length} keys from IndexedDB`, loadedKeys);
            } else {
              log.info(LogCategory.STATE, 'No data found in IndexedDB');
            }
            this.initialized = true;
            // Notify listeners that storage is ready
            this.onReadyCallbacks.forEach(cb => cb());
            this.onReadyCallbacks = [];
            resolve();
          }
        };

        request.onerror = () => {
          log.error(LogCategory.STATE, 'Failed to load data from IndexedDB', request.error);
          resolve();
        };
      });
    } catch (error) {
      log.error(LogCategory.STATE, 'Failed to initialize storage', error);
    }
  }

  get<T>(key: string, defaultValue: T): T {
    if (!this.initialized) {
      log.warn(LogCategory.STATE, `Accessing storage before initialization (key: ${key}). Data may not be loaded yet.`);
    }
    const value = this.cache.get(key);
    return value !== undefined ? value : defaultValue;
  }

  set<T>(key: string, value: T): boolean {
    this.cache.set(key, value);
    // Async save to IndexedDB (fire and forget with error logging)
    indexedDBStorage.set(key, value).catch((error) => {
      log.error(LogCategory.STATE, `Failed to persist ${key} to IndexedDB`, error);
    });
    return true;
  }

  remove(key: string): boolean {
    this.cache.delete(key);
    indexedDBStorage.remove(key).catch((error) => {
      log.error(LogCategory.STATE, `Failed to remove ${key} from IndexedDB`, error);
    });
    return true;
  }

  getCacheSize(): number {
    return this.cache.size;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

export const syncStorage = new SyncStorageWrapper();

/**
 * Initialize storage - call this early in app startup
 */
export async function initStorage(): Promise<void> {
  await syncStorage.init();
  log.info(LogCategory.STATE, 'IndexedDB storage initialized');

  // Log loaded data summary
  const cacheSize = syncStorage.getCacheSize();
  log.info(LogCategory.STATE, `Cache loaded: ${cacheSize} items in memory`);

  // Log storage estimate
  const estimate = await indexedDBStorage.getStorageEstimate();
  if (estimate) {
    const usageMB = (estimate.usage / 1024 / 1024).toFixed(2);
    const quotaMB = (estimate.quota / 1024 / 1024).toFixed(2);
    log.info(LogCategory.STATE, `Usage: ${usageMB}MB / ${quotaMB}MB`);
  }

  // Verify critical keys are loaded
  const criticalKeys = ['map-editor-projects', 'map-editor-floors', 'map-editor-viewer'];
  for (const key of criticalKeys) {
    const value = syncStorage.get(key, null);
    if (value !== null) {
      const dataType = Array.isArray(value) ? 'array' : typeof value;
      const dataSize = JSON.stringify(value).length;
      log.info(LogCategory.STATE, `✓ Loaded: ${key} (${dataType}, ${(dataSize / 1024).toFixed(2)}KB)`);
    } else {
      log.info(LogCategory.STATE, `- Not found: ${key}`);
    }
  }
}

/**
 * Migrate data from localStorage to IndexedDB
 */
export async function migrateFromLocalStorage(): Promise<void> {
  const keysToMigrate = [
    'map-editor-projects',
    'map-editor-floors',
    'map-editor-viewer',
  ];

  for (const key of keysToMigrate) {
    try {
      const localData = localStorage.getItem(key);
      if (localData) {
        const parsed = JSON.parse(localData);
        await indexedDBStorage.set(key, parsed);
        localStorage.removeItem(key);
        log.info(LogCategory.STATE, `Migrated ${key} from localStorage to IndexedDB`);
      }
    } catch (error) {
      log.error(LogCategory.STATE, `Failed to migrate ${key}`, error);
    }
  }
}
